import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { routes } from '@platform/core/lib/routes';
import { columnsFor, defaultColumns, stagesByFor } from '@platform/core/lib/viewSettings';
import { useAppDispatch, useAppSelector } from '@platform/state/hooks';
import { findModuleSlice } from '@platform/state/modules/moduleSliceRegistry';
import { selectLang, selectListState, selectModuleListState, selectSelection } from '@platform/state/selectors';
import { selectionChanged } from '@platform/state/slices/uiSlice';
import { defaultListViewState, listKeyOf, listStateChanged } from '@platform/state/slices/viewStateSlice';
import type { IListViewState, IModuleDefinition, IRecord, IRecordListModel, IRecordListParams, IRecordTypeDefinition, ViewKey } from '@platform/core/types';
import { miniFormStore } from '../form/mini/miniFormStore';
import { facetFieldOf } from '../menu/facetEntry';
import type { IMenuEntry } from '../menu/IMenuEntry';
import { useAdvancedDsl } from './advanced/useAdvancedDsl';
import type { IRecordListModelOptions } from './IRecordListModelOptions';
import { exportParamsOf, listQueryOf } from './listQuery';
import { recordOpenTab } from './recordOpenTab';
import { useListPage } from './useListPage';
import { useSavedView } from './savedViews/useSavedView';

const NO_REFETCH = (): void => undefined;

/**
 * Поєднує стан пункту меню, метадані та RTK Query у модель списку для представлень.
 * Стан списку (ключ `воркспейс|тип|пункт меню`) живе у зрізі пакета модуля (`module/<ключ>`), якщо пакет завантажено,
 * інакше — у платформному viewState; семантика скидання сторінки спільна (core/lib/listState).
 * Дані — useListPage: сторінка для таблиці, карток і редагування в рядку, увесь відфільтрований список для етапів, беклогу, розділеного
 * й календаря. `skipPage` — без запиту списку (аналітичні подання вантажать дані самі) завжди або для активного подання за функцією
 * (IRecordListModelOptions): page і error — undefined, isLoading — false, refetch нічого не робить. Вибране збережене подання (useSavedView)
 * додає свій фільтр і сортування до запиту, а його стовпці замінюють стовпці таблиці з метаданих; умови розширених фільтрів типу запису
 * і швидкі поля (useAdvancedDsl, спільні для всіх пунктів і подань модуля) додаються до фільтра запиту. `query` — той самий відбір без
 * сторінки й вкладки етапу для подань, що вантажать дані самі (useListParams); `domain` — лише фільтр і пресет пункту (лічильники «Фільтр»
 * сторінок-списків). Пункт «За …» (`facet:<поле>`) — подання «Етапи» з вкладками за цим полем.
 */
export function useRecordListModel(
  ws: string, module: IModuleDefinition, rt: IRecordTypeDefinition, entry: IMenuEntry, routeView?: string, options: IRecordListModelOptions = {},
): IRecordListModel {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const lang = useAppSelector(selectLang);
  const moduleSlice = findModuleSlice(module.module.key);
  const key = useMemo(() => ({ workspaceId: ws, recordType: rt.key, menuItem: entry.key }), [ws, rt.key, entry.key]);
  const defaultView = (entry.view ?? rt.views.default) as ViewKey;
  const state = useAppSelector(useMemo(
    () => (moduleSlice ? selectModuleListState(moduleSlice, listKeyOf(key), defaultView) : selectListState(key, defaultView)),
    [moduleSlice, key, defaultView],
  ));
  const selection = useAppSelector(useMemo(() => selectSelection(rt.key), [rt.key]));
  const view = (routeView as ViewKey | undefined) ?? state.view;
  const skipPage = typeof options.skipPage === 'function' ? options.skipPage(view) : (options.skipPage ?? false);

  const saved = useSavedView(ws, rt.key, state.savedViewId)?.definition;
  const advanced = useAdvancedDsl(ws, rt.key);
  const effective = useMemo(() => ({ ...state, view }), [state, view]);
  const stageBy = facetFieldOf(entry.key) ?? stagesByFor(rt);
  const query: IRecordListParams = useMemo(
    () => listQueryOf({ filter: entry.filter, preset: entry.preset }, effective, rt, saved, advanced, stageBy), [entry.filter, entry.preset, effective, rt, saved, advanced, stageBy],
  );
  const scope = useMemo(
    () => exportParamsOf(listQueryOf({ filter: entry.filter, preset: entry.preset }, { ...effective, stage: null }, rt, saved, advanced, stageBy)),
    [entry.filter, entry.preset, effective, rt, saved, advanced, stageBy],
  );

  const domain = useMemo(() => ({ ...(entry.filter ? { filter: entry.filter } : {}), ...(entry.preset ? { preset: entry.preset } : {}) }), [entry.filter, entry.preset]);

  const { page, isFetching, error, refetch } = useListPage(ws, rt.key, query, view, skipPage);

  const setState = useCallback((patch: Partial<IListViewState>) => {
    if (moduleSlice) dispatch(moduleSlice.listStateChanged({ key: listKeyOf(key), patch, defaults: defaultListViewState(defaultView) }));
    else dispatch(listStateChanged({ key, patch, defaultView }));
  }, [dispatch, moduleSlice, key, defaultView]);
  const setSelection = useCallback((ids: string[]) => dispatch(selectionChanged({ recordType: rt.key, ids })), [dispatch, rt.key]);
  const openRecord = useCallback(
    (r: IRecord) => navigate(routes.record(module.module.key, rt.key, r.id, recordOpenTab(rt, view))), [navigate, module.module.key, rt, view],
  );
  const openMini = useCallback((target: { module: string; recordType: string; id: string }) => miniFormStore.open({ ws, ...target }), [ws]);
  const openView = useCallback((v: ViewKey) => {
    setState({ view: v });
    navigate(routes.list(module.module.key, rt.key, v, params.get('item') ?? undefined));
  }, [setState, navigate, module.module.key, rt.key, params]);

  /** Колонки таблиці: стовпці вибраного подання → settings.table.columns → views.columns → усі поля inList; інші подання — свої поля з views.settings. */
  const columns = useMemo(() => (saved?.columns?.length ? saved.columns : columnsFor(rt, 'table', defaultColumns(rt))), [rt, saved]);

  return {
    ws, module, rt, lang, state: effective, page, isLoading: isFetching, error, columns, query: scope, domain, selection, setState, setSelection, openRecord, openMini, openView,
    refetch: skipPage ? NO_REFETCH : refetch,
  };
}
